import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, ChevronsLeft, ChevronsRight, Stethoscope, User, UserCog, Users } from 'lucide-react';
import { useAuthStore } from '@/auth/store';
import { useUserDefaultLanguage } from '@/i18n/useUserDefaultLanguage';
import { useClinicianClinics, useCurrentClinician } from '@/hooks/queries';
import { useOpenPanelViews } from '@/hooks/useOpenPanelViews';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENTS, track, useAnalyticsUserIdentity, useSentryRouteTracking } from '@/lib/analytics';
import { Button } from './ui/button';
import { PreferencesMenu } from './PreferencesMenu';
import { useApplyStoredTextSize } from '@/hooks/useTextSize';
import { useApplyStoredMealThumbnailSize } from '@/hooks/useMealThumbnailSize';
import { useApplyStoredFoodLogSplit } from '@/hooks/useFoodLogSplit';
import { useTrackActivePatient, readActivePatientPath } from '@/hooks/useActivePatient';
import { useApplyStoredAppFont } from '@/hooks/useAppFont';
import { useSidebarCollapsed, SIDEBAR_COLLAPSE_MAX_WIDTH } from '@/hooks/useSidebarCollapsed';
import { FeedbackDialog } from './FeedbackDialog';
import { BrandMark } from './BrandMark';
import { PortalSwitchLink } from './PortalSwitchLink';
import { cn } from '@/lib/utils';

// `match` is the route prefix that lights the item as active (so the Patients
// item stays highlighted on any /patients/:id sub-tab even though its href may
// point at a specific patient).
const navItems = [
  { to: '/patients', match: '/patients', icon: Users, label: 'Patients' },
  // Clinicians management lives between Patients and Profile — it's a
  // moderately-used admin surface (less than Patients, more than Resources).
  { to: '/clinicians', match: '/clinicians', icon: Stethoscope, label: 'Clinicians' },
  // Profile sits between Patients and Resources per UX preference — most
  // clinicians visit Patients constantly and Profile rarely, so a single
  // arm's-reach click from the most common entry point is the sweet spot.
  { to: '/account', match: '/account', icon: UserCog, label: 'Profile' },
  { to: '/resources', match: '/resources', icon: BookOpen, label: 'Resources' },
];


export const PageLayout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const clinician = useAuthStore((s) => s.clinician);
  const setClinician = useAuthStore((s) => s.setClinician);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Apply clinician.locale to i18next once the user is loaded — mirrors v1's
  // UserDefaultLanguageHandler. Lives behind ProtectedRoute so it only runs
  // after the user is authenticated.
  useUserDefaultLanguage();
  // Re-apply text-size preference to <html> on every layout mount —
  // initial state lives in localStorage, but the attribute can be
  // lost across hot reloads / route remounts in dev.
  useApplyStoredTextSize();
  useApplyStoredMealThumbnailSize();
  useApplyStoredFoodLogSplit();
  // Remember the patient being viewed so the Patients nav can return here
  // after a detour to Profile / Clinicians / Resources.
  useTrackActivePatient();
  useApplyStoredAppFont();

  // Analytics. View tracking + identity hooks live at the layout level so
  // every authed page is covered exactly once. The identity hook reads
  // clinician.clinics via useClinicianClinics so the clinic_ids tag is
  // populated for Sentry and OpenPanel without each call site forwarding it.
  const clinicsForIdentity = useClinicianClinics(clinician?.id);
  useAnalyticsUserIdentity(clinician, clinicsForIdentity.data ?? null);
  // When the clinician is already loaded, identify before the page-view
  // effect so the initial view can carry that OpenPanel profile.
  useOpenPanelViews(clinician?.id);
  useSentryRouteTracking(location.pathname);

  // Always-on clinician fetch. Lives at the layout level (not just on
  // PatientList) so anyone landing directly on `/patients/:id/...` or hitting
  // a state where the auth-store clinician got cleared (resetFlags, hot
  // reload, etc.) still gets re-populated. Without this, all flag-gated
  // buttons silently disappear because `useClinicianFlag` reads `clinician?.flags`.
  const clinicianQuery = useCurrentClinician();
  useEffect(() => {
    const fetched = clinicianQuery.data;
    if (!fetched) return;
    if (clinician?.id === fetched.id && clinician?.flags) return;
    setClinician({
      id: fetched.id,
      hospital_id: fetched.hospital_id,
      hospital_ids: fetched.hospital_ids,
      first_name: fetched.first_name,
      last_name: fetched.last_name,
      email: fetched.email,
      flags: fetched.flags,
      locale: fetched.locale ?? undefined,
      is_internal: fetched.is_internal,
    });
  }, [clinicianQuery.data, clinician, setClinician]);

  // ≤1365px is wide enough that the sidebar is already collapsed by the time
  // the Summary's Average Plate + Med Score pair is forced onto separate lines.
  //
  // NOTE: this branch previously hard-coded ≤1100px instead, so a 1080p /
  // 1366 laptop would keep the nav LABELS (clinicians scan by word, not
  // icon). Main's shared hook and constant win here rather than diverging
  // one call site from the rest of the app — but the 1080p case is what this
  // overhaul exists to fix, so the threshold needs confirming on a real
  // 1080p screen before this merges.
  const [collapsed, setCollapsed] = useSidebarCollapsed(SIDEBAR_COLLAPSE_MAX_WIDTH);

  const onToggle = () => setCollapsed((c) => !c);

  // Signed-in clinician label for the rail footer.
  const clinicianName = clinician
    ? `${clinician.first_name ?? ''} ${clinician.last_name ?? ''}`.trim()
    : '';
  const clinicianEmail = clinician?.email ?? '';
  // Tooltip used in the collapsed rail (where the text can't fit) and as a
  // hover hint on the expanded email line.
  const loggedInTooltip = clinicianEmail
    ? t('Logged in as {{who}}', { who: clinicianEmail })
    : clinicianName;

  return (
    // h-screen + overflow-hidden makes the shell exactly viewport-tall so the
    // <main> below (overflow-y-auto) becomes the actual scroll container.
    // Previously min-h-screen let the shell grow with content, so the DOCUMENT
    // scrolled and main never did — which broke position:sticky inside main
    // (e.g. the docked RxAssistant panel). Bonus: the sidebar + header now stay
    // put while content scrolls.
    <div className="flex h-screen overflow-hidden">
      <aside
        className={cn(
          'flex flex-col border-r bg-card transition-[width] duration-150',
          collapsed ? 'w-14' : 'w-56',
        )}
      >
        {/* Header carries the brand only — the collapse toggle moved
            into the nav below so it doesn't overlap the BrandMark
            when the sidebar is collapsed (the 56px collapsed width
            couldn't fit both the 28px mark AND the 32px toggle). */}
        <div className="flex h-14 items-center border-b px-2">
          <Link
            to="/patients"
            className="flex min-w-0 items-center gap-2 px-1 font-semibold"
            aria-label="RxFood"
          >
            <BrandMark className="h-7 w-7" />
            {!collapsed && <span className="truncate">RxFood</span>}
          </Link>
          {/* Casual "still in beta" disclaimer — a small pill beside the brand
              (expanded sidebar only; no room when collapsed). Native title
              tooltip carries the full note. */}
          {!collapsed && (
            <span
              className="ml-1.5 shrink-0 cursor-default rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide text-amber-700"
              title={t('This portal is in beta. Features are still changing. Thanks for bearing with us!')}
            >
              {t('Beta')}
            </span>
          )}
        </div>
        <nav className="flex-1 space-y-1 p-2">
          {/* Collapse / expand toggle — first item in the nav so it
              sits directly above the Patients icon. Right-aligned when
              expanded (chevron points where the panel will travel),
              centered when collapsed (matches the centered nav icons). */}
          <div className={cn('flex pb-1', collapsed ? 'justify-center' : 'justify-end')}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggle}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            </Button>
          </div>
          {navItems.map((item) => {
            // Patients navigates to the last-viewed patient (and tab) when one
            // is remembered, otherwise the list. Active highlight keys off the
            // route prefix so it stays lit across every patient sub-tab.
            const to =
              item.match === '/patients' ? readActivePatientPath() ?? item.to : item.to;
            const isActive =
              location.pathname === item.match ||
              location.pathname.startsWith(`${item.match}/`);
            return (
              <Link
                key={item.to}
                to={to}
                title={collapsed ? item.label : undefined}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  collapsed && 'justify-center px-2',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>
        {/* Signed-in clinician, pinned to the bottom of the rail. Expanded:
            a small "Logged in as" label with the name + email. Collapsed
            (56px — e.g. the auto-collapse at ≤1100px):
            a user icon whose tooltip carries the same "Logged in as {email}"
            since the text can't fit. */}
        {/* Preferences sits directly above "Logged in as" — tool-level
            controls next to who the tool thinks you are. Rendered whether or
            not the clinician has loaded, so log out stays reachable even if
            the profile fetch failed. */}
        <div className={cn('border-t p-2', collapsed && 'px-1')}>
          <PreferencesMenu
            collapsed={collapsed}
            onOpenFeedback={() => setFeedbackOpen(true)}
            onLogout={async () => {
              // Fire the LOGOUT event BEFORE the logout call clears the
              // OpenPanel identity — once the auth state clears, this event
              // would otherwise be filed under an anonymous profile.
              track(ANALYTICS_EVENTS.LOGOUT);
              await logout();
              navigate('/login', { replace: true });
            }}
          />
        </div>
        {clinician &&
          (collapsed ? (
            <div className="flex justify-center border-t p-3">
              <span className="text-muted-foreground" title={loggedInTooltip} aria-label={loggedInTooltip}>
                <User className="h-4 w-4" />
              </span>
            </div>
          ) : (
            <div className="border-t p-3">
              <p className="text-[0.625rem] uppercase tracking-wide text-muted-foreground">
                {t('Logged in as')}
              </p>
              {clinicianName && (
                <p className="truncate text-xs font-medium text-foreground" title={clinicianName}>
                  {clinicianName}
                </p>
              )}
              {clinicianEmail && (
                <p className="truncate text-[0.6875rem] text-muted-foreground" title={clinicianEmail}>
                  {clinicianEmail}
                </p>
              )}
            </div>
          ))}
      </aside>
      {/* min-w-0 lets this content column shrink to the viewport instead of
          refusing to go below its widest child (a wide data table). Without it,
          the column's default min-width:auto pushes <main> wider than the
          screen and the whole page scrolls sideways on smaller displays. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b">
          <div className="flex h-12 items-center gap-2 px-4 lg:h-14 lg:px-6">
          {/* Left slot — routes inject content here (e.g. the patient context
              bar) via HeaderLeftPortal; empty on pages that don't. */}
          <div id="app-header-left" className="flex min-w-0 flex-1 items-center gap-2" />
          {/* Route-injected header actions (e.g. the patient "Requiring action"
              indicator). Everything about the TOOL rather than the patient —
              feedback, text size, language, log out — moved to the sidebar's
              Preferences menu, and the invitation code to the Patients list.
              What's left in this row is the patient and their actions. */}
          {/* Only rendered for a user who is also a patient. */}
          <PortalSwitchLink to="patient" />
          <div
            id="app-header-actions"
            className="flex shrink-0 items-center gap-2 empty:hidden"
          />
          </div>
          {/* Full-width slot below the main header row — routes inject a compact
              context strip (e.g. the patient's diet/motivations) via
              HeaderBelowPortal. Spans under both the name and the Log out
              cluster; collapses to zero height (border included) when empty. */}
          <div id="app-header-below" className="border-t px-4 empty:hidden lg:px-6" />
        </header>
        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
};
